import { Thumbnail } from '@/store/Thumbnail'
import React, { ImgHTMLAttributes } from 'react'
import styles from './Picture.module.scss'

interface PictureProps extends ImgHTMLAttributes<HTMLImageElement> {
  thumbnail: Thumbnail
  alt: string
  className?: string
  lazyLoad?: boolean
}

export const Picture: React.FC<PictureProps> = ({
  thumbnail,
  alt,
  className = '',
  lazyLoad = true,
  ...imgProps
}) => {
  const loadingAttr = lazyLoad ? 'lazy' : 'eager'

  // Если нужно использовать picture элемент для автоматической адаптации
  if (thumbnail.desktop && thumbnail.tablet && thumbnail.mobile) {
    return (
      <picture className={className}>
        {/* Mobile (до 768px) */}
        <source
          media="(max-width: 768px)"
          srcSet={thumbnail.mobile.url}
          width={thumbnail.mobile.width}
          height={thumbnail.mobile.height}
        />

        {/* Tablet (768px - 1024px) */}
        <source
          media="(max-width: 1024px)"
          srcSet={thumbnail.tablet.url}
          width={thumbnail.tablet.width}
          height={thumbnail.tablet.height}
        />

        {/* Desktop (1025px и выше) */}
        <source
          media="(min-width: 1025px)"
          srcSet={thumbnail.desktop.url}
          width={thumbnail.desktop.width}
          height={thumbnail.desktop.height}
        />

        {/* Fallback - оригинальное изображение */}
        <img
          src={thumbnail.original?.url || thumbnail.desktop?.url || ''}
          alt={alt}
          loading={loadingAttr}
          className={styles.image}
          {...imgProps}
        />
      </picture>
    )
  }

  // Простой вывод с srcSet для адаптации
  const srcSet = [
    thumbnail.mobile && `${thumbnail.mobile.url} ${thumbnail.mobile.width}w`,
    thumbnail.tablet && `${thumbnail.tablet.url} ${thumbnail.tablet.width}w`,
    thumbnail.desktop && `${thumbnail.desktop.url} ${thumbnail.desktop.width}w`,
    thumbnail.original &&
      `${thumbnail.original.url} ${thumbnail.original.width}w`,
  ]
    .filter(Boolean)
    .join(', ')

  const sizes = [
    '(max-width: 640px) 357px',
    '(max-width: 1024px) 648px',
    '1013px',
  ].join(', ')

  const defaultSrc = thumbnail.original?.url || thumbnail.desktop?.url || ''

  return (
    <img
      src={defaultSrc}
      srcSet={srcSet || undefined}
      sizes={sizes}
      alt={alt}
      className={className}
      loading={loadingAttr}
      {...imgProps}
    />
  )
}
